import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowDownTrayIcon, 
  XMarkIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';
import { usePWA } from '../../contexts/PWAContext';
import { Button, Card, H3, Body } from '../ui';

interface InstallPromptProps {
  onClose?: () => void;
  variant?: 'banner' | 'modal' | 'card';
}

const InstallPrompt: React.FC<InstallPromptProps> = ({ 
  onClose, 
  variant = 'banner' 
}) => {
  const { isInstallable, installApp } = usePWA();
  const [isInstalling, setIsInstalling] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      const success = await installApp();
      if (success) {
        setIsVisible(false);
        onClose?.();
      }
    } catch (error) {
      console.error('Installation failed:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  if (!isInstallable || !isVisible) {
    return null;
  }

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const content = (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
          {isMobile ? (
            <DevicePhoneMobileIcon className="w-6 h-6 text-primary-600" />
          ) : (
            <ComputerDesktopIcon className="w-6 h-6 text-primary-600" />
          )}
        </div>
        <div>
          <H3 className="text-sm font-medium">Install Sprout</H3>
          <Body className="text-xs text-gray-600 dark:text-gray-400">
            Add to your {isMobile ? 'home screen' : 'desktop'} for quick access
          </Body>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <Button
          variant="primary"
          size="sm"
          onClick={handleInstall}
          disabled={isInstalling}
          isLoading={isInstalling}
          className="flex items-center space-x-2"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          <span>Install</span>
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="p-2"
        >
          <XMarkIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  if (variant === 'banner') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-md"
        >
          {content}
        </motion.div>
      </AnimatePresence>
    );
  }

  if (variant === 'card') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="border-2 border-primary-200 dark:border-primary-800">
            {content}
          </Card>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Modal variant
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="max-w-md w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="p-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <ArrowDownTrayIcon className="w-8 h-8 text-primary-600" />
              </div>
              
              <div>
                <H3 className="mb-2">Install Sprout</H3>
                <Body className="text-gray-600 dark:text-gray-400">
                  Install Sprout on your {isMobile ? 'device' : 'computer'} for offline access 
                  and a better experience. You can use all core features even without internet.
                </Body>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  variant="primary"
                  onClick={handleInstall}
                  disabled={isInstalling}
                  isLoading={isInstalling}
                  className="flex-1"
                >
                  Install App
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Not Now
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
